/* eslint-disable no-console */
import React, {
 ChangeEvent,
 useEffect,
 useState,
 useCallback,
} from 'react'
import {useForm} from 'react-hook-form'
// import {toast} from 'react-toastify'
import {Button} from 'helpers/ui-components/Button'
import {useTranslation} from 'react-i18next'
import {CheckboxField} from 'helpers'
import {
 fetchUserList,
 bulkApproveRejectUsers,
} from 'utils/api/auth/userManagementService'
import {
 IApproveReject,
 IUser,
 IUserAction,
} from 'models/UserManagement'
import {
 ColumnProps,
 IFormData,
 IFormField,
} from 'models/Common'
import KTTable from 'helpers/ui-components/KTTable'
import {DefaultApiResponse} from 'models/ApiResponse'
import {toast} from 'react-toastify'
import {formatDateForDisplay} from 'utils/helper'
import {KTRejectionPopup} from 'helpers/ui-components/KTRejectionPopup'
import {KTFilterBox} from 'helpers/ui-components/KTFilterBox'
import {KTSearchBox} from 'helpers/ui-components/KTSearchBox'
import {fetchFilterConfig} from 'utils/api/auth/commonService'
import {createFilterPayload} from 'utils/helpers/genericFilterForm'
type IProps = {
 accessDetails: IFormField
 loggedInUserId: number | undefined
 actionDetails?: IUserAction | null
 setLoader: (newState: boolean) => void
 setActionDetails: (newState: IUserAction | null) => void
}

const Approvals = (props: IProps) => {
 const {t} = useTranslation()
 const [rejectionModel, setRejectionModel] = useState(false)
 const [currentPage, setCurrentPage] = useState(1)
 const [userListRes, setUserListRes] =
  useState<DefaultApiResponse>()
 const [itemsPerPage, setItemsPerPage] = useState(25)
 const [searchTerm, setSearchTerm] = useState('')
 const [filterConfig, setFilterConfig] = useState<
  IFormField[]
 >([])
 const [filterFormData, setFilterFormData] =
  useState<IFormField | null>({})
 const [filterCount, setFilterCount] = useState(0)
 const [userList, setUserList] = useState<IUser[]>([])
 const [tableColList, setTableColList] = useState<
  Array<ColumnProps<IUser>>
 >([])
 const {register, getValues, setValue, reset} = useForm({
  mode: 'all',
  reValidateMode: 'onChange',
 })
 const approvalUserAccess =
  props.accessDetails?.approvals?.section
 const userDetailsAccess =
  props.accessDetails?.userDetails?.section

 const {reset: resionFormReset} = useForm({
  mode: 'all',
  reValidateMode: 'onChange',
 })

 const getFilterConfig = useCallback(async () => {
  try {
   const res = await fetchFilterConfig({
    filter_type: 'USER',
   })
   if (res) {
    setFilterConfig(res?.applicableFilters ?? [])
   }
  } catch (error) {
   setFilterConfig([])
  }
 }, [])

 useEffect(() => {
  void getFilterConfig()
 }, [getFilterConfig])

 const getUserList = async (
  pageNo = 1,
  pageSize = 10,
  signal?: AbortSignal
 ) => {
  props.setLoader(true)
  try {
   let query: IFormField = {}
   let appliedFilters = 0
   if (filterConfig?.length) {
    const payload = await createFilterPayload(
     filterConfig,
     filterFormData
    )
    const {totalAppliedFilters, ...restPayload} = payload
    appliedFilters =
     typeof totalAppliedFilters === 'number'
      ? totalAppliedFilters
      : Number(totalAppliedFilters ?? 0)
    query = restPayload as IFormField
   }
   const trimmedSearch = searchTerm.trim()
   if (trimmedSearch) {
    query = {
     ...query,
     search: trimmedSearch,
    }
   }
   const res: DefaultApiResponse | null =
    await fetchUserList(
     'phone_no_verified',
     pageNo,
     pageSize,
     Object.keys(query).length ? query : undefined,
     signal
    )
   if (res) {
    setUserListRes(res)
    setUserList(res.results ?? [])
   }
   setFilterCount(appliedFilters)
  } finally {
   props.setLoader(false)
  }
 }

 useEffect(() => {
  if (currentPage !== 1) {
   setCurrentPage(1)
  }
  const abortController = new AbortController()

  void getUserList(1, itemsPerPage, abortController.signal)

  return () => {
   abortController.abort()
  }
 }, [filterFormData, searchTerm, itemsPerPage])

 const handlePageChange = async (pageNo: number) => {
  setCurrentPage(pageNo)
  await getUserList(pageNo, itemsPerPage)
 }

 const handleItemsPerPageChange = (pageSize: number) => {
  setItemsPerPage(pageSize)
  setCurrentPage(1)
 }

 const handleSelectAll = useCallback(
  async (e: ChangeEvent<HTMLInputElement>) => {
   const isChecked = e.target.checked
   userList.forEach((item) => {
    setValue(`userIds.${item.id}`, isChecked)
   })
  },
  [setValue, userList]
 )

 async function getSelectedUserList() {
  const users: number[] = []
  userList.forEach((item) => {
   const val = getValues(`userIds.${item.id}`)
   if (val) {
    users.push(item.id)
   }
  })
  if (users.length === 0) {
   toast.error(t('common.pleaseSelectUser'))
   return []
  }
  return users
 }

 async function submitRejectionForm(formData: IFormData) {
  const users: number[] = await getSelectedUserList()
  if (users.length > 0) {
   props.setLoader(true)
   const payload: IApproveReject = {
    user_ids: users,
    status: 'rejected',
    rejection_reason_id: Number(formData.rejectionIds),
   }
   const res: DefaultApiResponse | null =
    await bulkApproveRejectUsers(payload)
   props.setLoader(false)
   if (res) {
    reset()
    toast.success(res.message)
    setRejectionModel(false)
    resionFormReset()
    await getUserList(currentPage, itemsPerPage)
   }
  }
 }

 async function handleRejectBtnClick() {
  const users: number[] = await getSelectedUserList()
  if (users.length > 0) {
   setRejectionModel(true)
  }
 }

 async function handleApproveBtnClick() {
  const users: number[] = await getSelectedUserList()
  if (users.length > 0) {
   props.setLoader(true)
   const payload: IApproveReject = {
    user_ids: users,
    status: 'approved',
   }
   const res: DefaultApiResponse | null =
    await bulkApproveRejectUsers(payload)
   props.setLoader(false)
   if (res) {
    reset()
    toast.success(res.message)
    await getUserList(currentPage, itemsPerPage)
   }
  }
 }
 const handleActionBtnClick = useCallback(
  (userId: number) => {
   props.setActionDetails({
    userId: userId.toString(),
    actionType: 'approvals',
   })
  },
  [props.setActionDetails]
 )
 const renderTableData = useCallback(async () => {
  props.setLoader(true)

  const columns: Array<ColumnProps<IUser>> = [
   ...(approvalUserAccess?.approve?.canEdit ||
   approvalUserAccess?.reject?.canEdit
    ? [
       {
        key: 'select',
        title: (
         <div className='cursor-pointer text-[#4E83E4] font-semibold'>
          <CheckboxField
           type='checkbox'
           label=''
           className={`w-6 h-6 mt-1 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500 focus:ring-2 border-neutral-30`}
           wrapperClassName=''
           name={`selectAll`}
           id={`selectAll`}
           value={'1'}
           register={register('selectAll', {
            onChange: (e) => {
             void handleSelectAll(e)
            },
           })}
          />
         </div>
        ),
        render: (_: ColumnProps<IUser>, record: IUser) => {
         return (
          <div className='cursor-pointer text-[#4E83E4] font-semibold text-center'>
           <CheckboxField
            type='checkbox'
            label=''
            className={`w-6 h-6 mt-1 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500 focus:ring-2 border-neutral-30`}
            wrapperClassName=''
            value={record.id}
            name={`userIds-${record.id}`}
            key={`${record.id}`}
            id={`userIds-${record.id}`}
            register={register(`userIds.${record.id}`, {
             required: false,
            })}
           />
          </div>
         )
        },
       },
      ]
    : []),
   {
    key: 'srNo',
    title: t('userManagement.userListTable.srNo'),
    className: 'w-[80px] text-center',
    isSrNo: true,
   },
   {
    key: 'full_name',
    title: t('userManagement.userListTable.name'),
    render: (_, record) => {
     return (
      <button
       type='button'
       disabled={!userDetailsAccess}
       className={`${
        userDetailsAccess && 'text-[#4E83E4] underline'
       } w-full text-left disabled:bg-transparent`}
       onClick={() => {
        if (userDetailsAccess)
         void handleActionBtnClick(record.id)
       }}
      >
       {record.full_name}
      </button>
     )
    },
   },
   {
    key: 'organisation',
    title: t('userManagement.userListTable.firmName'),
   },
   {
    key: 'phone_number',
    title: t('userManagement.userListTable.mobile'),
    className: 'w-[80px]',
   },
   {
    key: 'created_at',
    title: t(
     'userManagement.userListTable.userCreationDate'
    ),
    render: (_, record) => {
     return formatDateForDisplay(record?.created_at)
    },
   },
   {
    key: 'stakeholder',
    title: t('userManagement.userListTable.stakeholder'),
   },
   {
    key: 'category',
    title: t('userManagement.userListTable.category'),
   },
   {
    key: 'user_category',
    title: t('userManagement.userListTable.userCategory'),
   },
   /*  {
    key: 'action',
    title: t('userManagement.userListTable.action'),
    render: (_, record) => {
     return (
      <div className='flex items-center justify-center w-full'>
       <KTSVG
        path={`/media/icons/trash.svg`}
        svgClassName={`w-5 h-5 cursor-pointer`}
        onClick={() => {
         void handleUserClick(record)
        }}
       />
      </div>
     )
    },
   }, */
  ]
  setTableColList(columns)
  props.setLoader(false)
 }, [
  handleActionBtnClick,
  handleSelectAll,
  register,
  props.setLoader,
  t,
  userDetailsAccess,
 ])

 useEffect(() => {
  if (currentPage) {
   void getUserList(currentPage, itemsPerPage)
  }
 }, [props.actionDetails])
 useEffect(() => {
  if (userList) {
   void renderTableData()
  }
 }, [renderTableData, userList])

 return (
  <>
   {rejectionModel && (
    <KTRejectionPopup
     title={t('userManagement.approvals.reasonTitle')}
     btnText={t('userManagement.approvals.confirm')}
     submitRejectionForm={submitRejectionForm}
     showRejectionModal={rejectionModel}
     reason={'user_registration'}
     setShowRejectionModal={setRejectionModel}
     setLoader={props.setLoader}
    />
   )}
   <div className='my-6'>
    <div className='grid md:grid-cols-2 items-center gap-4'>
     <KTSearchBox
      label={t('userManagement.approvals.search')}
      placeholder={t(
       'userManagement.approvals.searchPlaceholder'
      )}
      setSearchTerm={setSearchTerm}
     />
     {filterConfig?.length > 0 && (
      <div className='flex gap-4 items-center justify-end'>
       <KTFilterBox
        parentId='approvals'
        btnText={t('userManagement.approvals.filters')}
        containerClass='bg-white border-none mt-0 pl-0 justify-end'
        filterConfig={filterConfig}
        filterFormData={filterFormData ?? {}}
        setFilterFormData={setFilterFormData}
        filterCount={filterCount}
        setLoader={props.setLoader}
        showDateRangeBox={false}
       />
      </div>
     )}
    </div>
   </div>
   <div className='w-full'>
    <div className='flex items-center justify-end text-[#171B1F] bg-[#F5F8FA] text-xl font-bold rounded-lg p-4 mb-6'>
     {/* <div className='flex items-center '>
      <Button
       disabled={false}
       onClick={() => {}}
       color='white'
       className='text-center py-[9px] p-3 bg-white 
                          !rounded-lg text-sm !text-[#4E83E4] font-semibold border-[#E6EBF0]'
      >
       <KTSVG
        path={`/media/icons/filter.svg`}
        svgClassName={`w-4 h-4 text-white mr-2`}
       />
       {t('helperText.filter')}
      </Button>
     </div> */}
     <div className='flex items-center '>
      {approvalUserAccess?.reject?.canEdit && (
       <Button
        type='button'
        onClick={handleRejectBtnClick}
        disabled={false}
        color='blue'
        className='text-center py-[9px] px-6 bg-[#FFEBED] 
                     !rounded-lg text-base text-[#CC293F] font-semibold border-[#CC293F] hover:bg-[#CC293F] hover:text-white mr-4'
       >
        {t('userManagement.approvals.reject')}
       </Button>
      )}
      {approvalUserAccess?.approve?.canEdit && (
       <Button
        type='button'
        disabled={false}
        onClick={handleApproveBtnClick}
        color='blue'
        className='text-center py-[9px] px-6 bg-[#DAF5E6] text-[#0E7A41] 
                     !rounded-lg text-base font-semibold border-[#0E7A41] hover:bg-[#0E7A41] hover:text-white'
       >
        {t('userManagement.approvals.approve')}
       </Button>
      )}
     </div>
    </div>
    <div>
     <KTTable
      data={userList}
      columns={tableColList}
      headerClass='bg-[#80868C]'
      headerColClass='!border-0 text-left'
      colClass='text-[#33393D] !text-sm font-normal !border-0 !py-3'
      tableClass=''
      rowClass='even:bg-[#E6EBF0]'
      showPagination={true}
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalRecord={userListRes?.count}
      onPageChange={handlePageChange}
      setItemsPerPage={handleItemsPerPageChange}
     />
    </div>
   </div>
  </>
 )
}

export default Approvals
